import { useCallback, useEffect, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";

import { NotificationBell } from "@/features/notifications";
import { cn } from "@/shared/lib/cn";

import { BottomNav } from "./BottomNav";
import { Button } from "./Button";

export interface AppShellTab {
  to: string;
  label: string;
  icon: LucideIcon;
}

/**
 * Horizontally-scrollable tab strip used at md+ breakpoints. On mobile the
 * AppShell renders a thumb-reachable BottomNav instead — a phone with five
 * destinations crammed into a top strip is what the original "where's the
 * fifth tab" complaint was about.
 *
 * Bidirectional edge fades + auto-center-on-route-change still apply here
 * because at narrow tablet widths (md but not xl) the strip can still
 * overflow if labels are long.
 */
function TabStrip({ tabs }: { tabs: AppShellTab[] }) {
  const scrollerRef = useRef<HTMLElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const location = useLocation();

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateEdges();
    el.addEventListener("scroll", updateEdges, { passive: true });
    const ro = new ResizeObserver(updateEdges);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateEdges);
      ro.disconnect();
    };
  }, [updateEdges]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const active = scroller.querySelector<HTMLElement>('[aria-current="page"]');
    if (!active) return;
    const scrollerRect = scroller.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const delta =
      (activeRect.left + activeRect.right) / 2 -
      (scrollerRect.left + scrollerRect.right) / 2;
    if (Math.abs(delta) < 4) return;
    scroller.scrollBy({ left: delta, behavior: "smooth" });
  }, [location.pathname]);

  return (
    <div className="relative">
      <nav
        ref={scrollerRef}
        aria-label="Primary"
        className="mx-auto flex max-w-6xl items-center gap-1 overflow-x-auto scroll-smooth px-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map(({ to, label }) => (
          <NavLink
            key={to}
            to={to}
            end
            className={({ isActive }) =>
              cn(
                "relative whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors",
                isActive ? "text-primary" : "text-copy-muted hover:text-copy",
              )
            }
          >
            {({ isActive }) => (
              <>
                {label}
                <span
                  className={cn(
                    "absolute bottom-0 left-2 right-2 h-0.5 rounded-full transition-all",
                    isActive ? "bg-primary" : "bg-transparent",
                  )}
                />
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-surface to-transparent transition-opacity duration-200 lg:hidden",
          canScrollLeft ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-surface to-transparent transition-opacity duration-200 lg:hidden",
          canScrollRight ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
}

interface AppShellProps {
  tabs: AppShellTab[];
  userEmail: string | undefined;
  userLabel?: string;
  roleLabel?: string;
  onLogout: () => void;
  children: React.ReactNode;
}

export function AppShell({
  tabs,
  userEmail,
  userLabel,
  roleLabel,
  onLogout,
  children,
}: AppShellProps) {
  return (
    <div className="min-h-screen bg-background text-copy">
      <header className="sticky top-0 z-40 border-b border-border bg-surface/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-4">
          <div className="leading-tight">
            <p className="text-base font-semibold tracking-display-tight text-copy">OneShift</p>
            {roleLabel && (
              <p className="text-[11px] uppercase tracking-wider text-copy-subtle">{roleLabel}</p>
            )}
          </div>

          <div className="flex items-center gap-3">
            {userEmail && <NotificationBell />}
            <div className="hidden text-right sm:block">
              {userLabel && <p className="text-sm font-medium text-copy">{userLabel}</p>}
              {userEmail && <p className="text-xs text-copy-muted">{userEmail}</p>}
            </div>
            <Button variant="ghost" size="sm" onClick={onLogout}>
              Sign out
            </Button>
          </div>
        </div>

        {/* Top tab strip — md+ only. On mobile, navigation lives in the
            thumb-reachable BottomNav at the bottom of the viewport. */}
        <div className="hidden md:block">
          <TabStrip tabs={tabs} />
        </div>
      </header>

      {/* pb-24 reserves room for the fixed BottomNav on mobile so page content
          isn't hidden behind it. The bottom nav itself adds env(safe-area-inset-bottom)
          on top of that for iOS devices with a home indicator. */}
      <main className="mx-auto max-w-6xl px-6 py-8 pb-24 md:pb-8">{children}</main>

      <BottomNav items={tabs} />
    </div>
  );
}
